/**
 * The palette charts read from — deliberately not the one badges read from.
 *
 * `STATUS_MAP` gives 19 statuses only 7 colours, and 8 of them share the same
 * `#D3D3D3`. As a badge palette that is good design: on a table row, grey means
 * "nothing to do here", amber "moving", red "problem", and the label right next
 * to it says which state exactly. A badge communicates **urgency**.
 *
 * A chart has the opposite need. In a donut the colour is the only signal, so
 * eight identical greys are eight slices nobody can tell apart — which is why
 * the dashboard looked like a grey ring. A chart needs **identity**.
 *
 * So: same five families as `DeliveryStatusEnum` groups them, so a glance still
 * reads "blue = not out yet, violet = in the warehouse, amber = moving, green =
 * delivered, red = problem", and a longer look separates the five ambers from
 * each other.
 */

/** Delivered wears the brand green, `--ninjaGreen`. */
const BRAND_GREEN = '#00d693';

export const CHART_COLORS = {
  // Aún no sale — azules.
  pending: '#93c5fd',
  waiting_for_assignment: '#60a5fa',
  created_on_courier: '#2563eb',

  // En bodega — violetas.
  assigned: '#c4b5fd',
  packed: '#a78bfa',
  fulfilled: '#8b5cf6',
  withdrawn: '#6d28d9',
  delivered_to_courier: '#4c1d95',

  // En movimiento — ámbares y naranjas.
  ready_for_pickup: '#fde68a',
  in_cd: '#fbbf24',
  dropped_at_hub: '#f59e0b',
  received_at_hub: '#d97706',
  in_transit: '#fb923c',
  on_the_way: '#ea580c',

  // Llegó.
  delivered: BRAND_GREEN,

  // Terminó mal — rojos y rosas. `cancelled` es el más claro de la familia:
  // sigue siendo un final malo, pero no es un fallo de la operación.
  cancelled: '#fda4af',
  returning: '#fb7185',
  failed: '#ef4444',
  returned: '#e11d48',
  compensated: '#be123c',
  damaged: '#881337',
};

/**
 * The five families, for anything that groups rather than lists — the stacked
 * bars of the daily chart, for one.
 */
export const STATUS_FAMILY = {
  pending: 'initial',
  waiting_for_assignment: 'initial',
  created_on_courier: 'initial',
  assigned: 'warehouse',
  packed: 'warehouse',
  fulfilled: 'warehouse',
  withdrawn: 'warehouse',
  delivered_to_courier: 'warehouse',
  ready_for_pickup: 'moving',
  in_cd: 'moving',
  dropped_at_hub: 'moving',
  received_at_hub: 'moving',
  in_transit: 'moving',
  on_the_way: 'moving',
  delivered: 'delivered',
  cancelled: 'failed',
  returning: 'failed',
  failed: 'failed',
  returned: 'failed',
  compensated: 'failed',
  damaged: 'failed',
};

export const FAMILY_LABEL = {
  initial: 'Por salir',
  warehouse: 'En bodega',
  moving: 'En movimiento',
  delivered: 'Entregados',
  failed: 'Con problema',
};

export const FAMILY_COLOR = {
  initial: '#60a5fa',
  warehouse: '#a78bfa',
  moving: '#f59e0b',
  delivered: BRAND_GREEN,
  failed: '#ef4444',
};

/** The order families are stacked in: the shipment's own journey. */
export const FAMILY_ORDER = ['initial', 'warehouse', 'moving', 'delivered', 'failed'];

/**
 * The same neutral the rest of the app falls back to for an unknown status. A
 * state added on the backend should look unfamiliar, not be dropped.
 *
 * @param {string} status
 * @returns {string}
 */
export const getChartColor = (status) => CHART_COLORS[status] || '#9ca3af';

/** @param {string} status */
export const getStatusFamily = (status) => STATUS_FAMILY[status] || 'initial';
